import { useEffect, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import BountyBadge from "@/components/BountyBadge";
import MatchScoreGauge from "@/components/MatchScoreGauge";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, Briefcase, Building2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function JobDetail() {
    const { id } = useParams();
    const { search } = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [match, setMatch] = useState(null);
    const [submissionId, setSubmissionId] = useState(null);
    const [form, setForm] = useState({
        candidate_name: "",
        candidate_title: "",
        years_experience: "",
        skills: "",
        linkedin: "",
        current_comp: "",
        target_comp: "",
        location: "",
        summary: "",
    });

    useEffect(() => {
        api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(false));
    }, [id]);

    if (job === null) return <div className="py-24 text-center text-[#A3A3A3]">Loading…</div>;
    if (job === false) return <div className="py-24 text-center text-[#A3A3A3]">Mandate not found.</div>;

    const formatSalary = (a) => `$${(a / 1000).toFixed(0)}k`;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!user || typeof user !== "object") {
            toast.error("Please sign in as a recruiter to submit candidates.");
            navigate(`/login?next=/jobs/${id}`);
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                job_id: id,
                candidate_name: form.candidate_name.trim(),
                candidate_title: form.candidate_title.trim(),
                years_experience: parseInt(form.years_experience || "0", 10),
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
                linkedin: form.linkedin.trim() || null,
                current_comp: form.current_comp ? parseInt(form.current_comp, 10) : null,
                target_comp: form.target_comp ? parseInt(form.target_comp, 10) : null,
                location: form.location.trim() || null,
                summary: form.summary.trim(),
            };
            const { data } = await api.post("/submissions", payload);
            setMatch(data.match);
            setSubmissionId(data.id);
            toast.success("Candidate submitted. Match Overview generated.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    }

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div data-testid="job-detail-page" className="mx-auto max-w-[1200px] px-6 py-12 md:px-10">
            <Link
                to={{ pathname: "/jobs", search }}
                className="caption inline-flex items-center gap-2 text-[#A3A3A3] transition-colors hover:text-[#FAFAFA]"
                data-testid="back-to-jobs"
            >
                <ArrowLeft size={14} /> All Mandates
            </Link>

            <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_360px]">
                <div>
                    <div className="caption text-[var(--brand-accent)]">{job.region} · {job.seniority}</div>
                    <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA] sm:text-6xl leading-[1]">
                        {job.title}
                    </h1>
                    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[#A3A3A3]">
                        <span className="inline-flex items-center gap-2"><Building2 size={16} /> {job.company}</span>
                        <span className="inline-flex items-center gap-2"><MapPin size={16} /> {job.location}</span>
                        <span className="inline-flex items-center gap-2"><Briefcase size={16} /> {formatSalary(job.salary_min)}–{formatSalary(job.salary_max)}</span>
                    </div>

                    <div className="mt-10 hairline pb-2">
                        <div className="caption text-[#737373]">The Mandate</div>
                    </div>
                    <p className="mt-6 text-lg font-light leading-relaxed text-[#FAFAFA]/90 whitespace-pre-line">
                        {job.description}
                    </p>

                    <div className="mt-12 hairline pb-2">
                        <div className="caption text-[#737373]">Required Capabilities</div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {(job.skills || []).map((s) => (
                            <span key={s} className="rounded-sm border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm text-[#FAFAFA]">{s}</span>
                        ))}
                    </div>
                </div>

                <aside className="space-y-6">
                    <div className="rounded-md border border-white/5 bg-[#141414]/60 p-7 backdrop-blur-xl sovereign-shadow">
                        <div className="caption text-[#737373]">Placement Bounty</div>
                        <div className="mt-3 font-serif text-5xl tracking-display" style={{ color: "var(--brand-accent)" }}>
                            ${(job.bounty / 1000).toFixed(0)}k
                        </div>
                        <p className="mt-3 text-sm text-[#A3A3A3]">
                            Paid upon successful placement. No clawbacks. Wired within 14 days of start date.
                        </p>
                        {!showForm && !match && (
                            <button
                                data-testid="btn-submit-candidate"
                                onClick={() => setShowForm(true)}
                                className="mt-6 w-full rounded-sm py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)]"
                                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                            >
                                Submit a Candidate
                            </button>
                        )}
                    </div>

                    {match && (
                        <MatchOverviewPanel match={match} submissionId={submissionId} />
                    )}
                </aside>
            </div>

            {showForm && !match && (
                <div className="mt-16 rounded-md border border-white/5 bg-[#141414]/60 p-8 backdrop-blur-xl">
                    <div className="mb-6">
                        <div className="caption text-[var(--brand-accent)]">Candidate Submission</div>
                        <h2 className="mt-2 font-serif text-3xl tracking-display text-[#FAFAFA]">
                            Tell us about your candidate
                        </h2>
                        <p className="mt-2 text-sm text-[#A3A3A3]">
                            We'll generate a Match Overview against this mandate in under 10 seconds.
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="grid gap-5 md:grid-cols-2">
                        <Input testId="input-candidate_name" label="Full name" value={form.candidate_name} onChange={update("candidate_name")} required />
                        <Input testId="input-candidate_title" label="Current title" value={form.candidate_title} onChange={update("candidate_title")} required />
                        <Input testId="input-years_experience" label="Years experience" type="number" value={form.years_experience} onChange={update("years_experience")} required />
                        <Input testId="input-location" label="Location" value={form.location} onChange={update("location")} />
                        <Input testId="input-skills" label="Skills (comma-separated)" value={form.skills} onChange={update("skills")} required wide />
                        <Input testId="input-linkedin" label="LinkedIn URL" value={form.linkedin} onChange={update("linkedin")} />
                        <Input testId="input-current_comp" label="Current comp (USD)" type="number" value={form.current_comp} onChange={update("current_comp")} />
                        <Input testId="input-target_comp" label="Target comp (USD)" type="number" value={form.target_comp} onChange={update("target_comp")} />
                        <div className="md:col-span-2">
                            <label className="caption text-[#737373]">Executive summary</label>
                            <textarea
                                data-testid="input-summary"
                                required
                                minLength={10}
                                rows={5}
                                value={form.summary}
                                onChange={update("summary")}
                                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:bg-white/[0.05] focus:outline-none"
                                placeholder="Why this candidate, briefly."
                            />
                        </div>
                        <div className="md:col-span-2 flex gap-3">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="btn-run-match"
                                className="inline-flex items-center justify-center gap-2 rounded-sm px-8 py-3.5 text-sm font-medium transition-all duration-300 disabled:opacity-50"
                                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                            >
                                {submitting ? "Analyzing…" : "Run Match Overview"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="rounded-sm border border-white/10 px-6 py-3.5 text-sm text-[#FAFAFA] hover:bg-white/5"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Input({ label, testId, wide, ...rest }) {
    return (
        <div className={wide ? "md:col-span-2" : undefined}>
            <label className="caption text-[#737373]">{label}</label>
            <input
                data-testid={testId}
                {...rest}
                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:bg-white/[0.05] focus:outline-none"
            />
        </div>
    );
}

function MatchOverviewPanel({ match, submissionId }) {
    const bars = [
        { key: "skills_score", label: "Skills" },
        { key: "experience_score", label: "Experience" },
        { key: "comp_alignment_score", label: "Compensation" },
        { key: "location_score", label: "Location" },
    ];
    return (
        <div data-testid="match-overview-panel" className="rounded-md border border-white/5 bg-[#141414]/60 p-7 backdrop-blur-xl sovereign-shadow">
            <div className="caption text-[var(--brand-accent)]">Match Overview</div>
            <div className="my-5 flex justify-center">
                <MatchScoreGauge score={match.overall_score} testId={`match-score-gauge-${submissionId || "preview"}`} />
            </div>
            <div className="space-y-3">
                {bars.map(({ key, label }) => (
                    <div key={key}>
                        <div className="flex justify-between text-xs text-[#A3A3A3]">
                            <span>{label}</span>
                            <span className="font-mono text-[#FAFAFA]">{match[key]}</span>
                        </div>
                        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/5">
                            <div
                                className="h-full"
                                style={{
                                    width: `${match[key]}%`,
                                    background: "var(--brand-accent)",
                                    boxShadow: "0 0 8px rgba(201,169,110,0.4)",
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
            {match.strengths?.length > 0 && (
                <div className="mt-5">
                    <div className="caption text-[#737373]">Strengths</div>
                    <ul className="mt-2 space-y-1.5 text-sm text-[#FAFAFA]/90">
                        {match.strengths.map((s, i) => (
                            <li key={i} className="flex gap-2"><span style={{ color: "var(--brand-accent)" }}>•</span>{s}</li>
                        ))}
                    </ul>
                </div>
            )}
            {match.gaps?.length > 0 && (
                <div className="mt-4">
                    <div className="caption text-[#737373]">Gaps</div>
                    <ul className="mt-2 space-y-1.5 text-sm text-[#FAFAFA]/90">
                        {match.gaps.map((s, i) => (
                            <li key={i} className="flex gap-2"><span className="text-[#7A2F2F]">•</span>{s}</li>
                        ))}
                    </ul>
                </div>
            )}
            {match.recommendation && (
                <div className="mt-4 rounded-sm border border-white/5 bg-black/30 p-3 text-xs italic text-[#A3A3A3]">
                    {match.recommendation}
                </div>
            )}
            {match.ai_powered === false && (
                <div className="mt-3 caption text-[#737373]">Deterministic scoring (AI fallback)</div>
            )}
        </div>
    );
}
